import {
  Accordion,
  AccordionPanel,
  Anchor,
  Box,
  Button,
  CheckBox,
  DateInput,
  FileInput,
  FormField,
  Header,
  Heading,
  Image,
  Layer,
  NameValueList,
  NameValuePair,
  Page,
  PageContent,
  RadioButtonGroup,
  Text,
  TextArea,
  TextInput,
} from "grommet";
import { Next, StatusWarning } from "grommet-icons";
import { fromUrl } from "parse-domain";
import { phone } from "phone";
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BSON } from "realm-web";
import { useImmer } from "use-immer";
import { useEmbedded } from "../hooks/useEmbedded";
import { isDomain, okayActor, okayDomain } from "../utils/validate";
import { CheckStatus } from "./CheckStatus";
import { FormElement } from "./FormElement";
import { LoadingIndicator } from "./LoadingIndicator";
import { RealmContext } from "./providers/RealmContext";
import { AbuseFormConfig, ClientConfigContext } from "./wrappers/ClientWrapper";

interface AbuseType {
  id: string;
  class: "Content" | "Activity";
  type: string;
  showType?: string;
  subType?: string;
  label: string;
}

export interface AbuseGroups {
  heading: string;
  preText: string | ReactNode;
  examples: (string | ReactNode)[];
  postText: string | ReactNode;
  types: string[];
}

interface AbuseReport {
  phase: number;
  client: string;
  abuse: {
    type: AbuseType | undefined;
    date: Date;
    ongoing: boolean;
    description: string;
  };
  details: {
    target?: string;
    headers?: string;
    body?: string;
    filename?: string;
    malware?: string;
    md5?: string;
    sha1?: string;
    smsNumber?: string;
    url?: string;
    copyrightHolder?: string;
    copyrightDescription?: string;
    copyrightAcknowledgement?: boolean;
    copyrightSignature?: string;
    copyrightSworn?: boolean;
  };
  reporter: {
    realmId: string;
    email: string;
    fullName?: string;
    phoneNumber?: string;
  };
  actor: {
    url: string;
    okay: boolean;
  };
  evidence: File[];
}

interface Receipt {
  okay: Boolean;
  errors: string[];
  _id: BSON.ObjectId | null;
  noId?: Boolean;
}

const abuseTypes: AbuseType[] = [
  {
    id: "csam",
    class: "Content",
    type: "CSAM",
    label: "Child Sexual Abuse Material",
  },
  {
    id: "nocsam",
    class: "Content",
    type: "CSAM",
    label: "Child Sexual Abuse Material",
  },
  {
    id: "noncii",
    class: "Content",
    type: "NCII",
    showType: "Non-Consensual Intimate Image",
    label: "Non-Consensual Intimate Images",
  },
  {
    id: "violence",
    class: "Content",
    type: "Violence",
    showType: "Violent Content",
    label: "Violent or disturbing imagery, excluding CSAM",
  },
  {
    id: "violence2",
    class: "Content",
    type: "Violence",
    showType: "Violent Content",
    label: "Violent or disturbing imagery",
  },
  {
    id: "pharmaceutical",
    class: "Content",
    type: "Pharmaceutical",
    label: "Suspicious pharmaceutical content",
  },
  {
    id: "pii",
    class: "Content",
    type: "PIIExposure",
    showType: "Personal Information Exposure",
    label: "Exposure of personal information or Doxing",
  },
  {
    id: "malware",
    class: "Content",
    type: "Malware",
    label: "Virus or malicious software",
  },
  {
    id: "fraud",
    class: "Content",
    type: "Fraud",
    label: "Email or website with fraudulent activity",
  },
  {
    id: "cracking",
    class: "Content",
    type: "Cracking",
    label: "Promotion of computer or network hacking",
  },
  {
    id: "phishing",
    class: "Content",
    type: "Phishing",
    label: "Email or website requesting personal information",
  },
  {
    id: "pharming",
    class: "Content",
    type: "Phishing",
    subType: "Pharming",
    showType: "Pharming (Phishing)",
    label: "Pharming (website spoofing)",
  },
  {
    id: "spam",
    class: "Activity",
    type: "Spam",
    subType: "Email",
    showType: "Email Spam",
    label: "Unsolicited email",
  },
  {
    id: "sms",
    class: "Activity",
    type: "Spam",
    subType: "SMS",
    showType: "SMS Spam",
    label: "Unsolicited text message",
  },
  {
    id: "comment",
    class: "Activity",
    type: "Spam",
    subType: "Online",
    showType: "Forum Spam",
    label: "Unsolicited online comment",
  },
  {
    id: "copyright",
    class: "Content",
    type: "Copyright",
    label: "Copyright infringement",
  },
  { id: "botnet", class: "Content", type: "Botnet", label: "Botnet activity" },
  {
    id: "ddos",
    class: "Content",
    type: "DDoS",
    showType: "Distributed Denial of Service (DDoS)",
    label: "Distributed denial of service (DDos)",
  },
  {
    id: "loginattack",
    class: "Content",
    type: "LoginAttack",
    label: "Login Attack",
  },
];

const bucket = import.meta.env.VITE_STORAGE_BUCKET;

const phishingRadioOptions = ["Yes", "No"];
const dmcaHostingRadioOptions = ["Yes", "No"];

// utility to convert heading to kebab-case for anchor ids
function toKebab(str: string) {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .toLowerCase();
}

const dateInputFormat = getDateInputFormat();

export function NewReport() {
  // track which AccordionPanel(s) are open
  const [activeIndex, setActiveIndex] = useState<number[]>([]);
  const [localAbuseTypes, setLocalAbuseTypes] = useState(abuseTypes);
  // useEffect(() => {
  //   setLocalAbuseTypes(abuseTypes);
  // }, [abuseTypes]);

  const cancelSubmission = useRef(false);
  const [phishingOption, setTrademarkOption] = useState<string | undefined>(
    undefined,
  );
  const [dmcaHosting, setDmcaHosting] = useState<string | undefined>(undefined);
  const config = useContext(ClientConfigContext);
  const navigate = useNavigate();
  const { isEmbedded } = useEmbedded();
  const { realmUser: user, callFunction } = useContext(RealmContext);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const initialReport: AbuseReport = useMemo(
    () => ({
      phase: 1,
      client: config.code,
      abuse: {
        type: undefined,
        date: new Date(),
        ongoing: true,
        description: "",
      },
      details: {},
      reporter: {
        realmId: user!.id,
        email: "",
      },
      actor: {
        url: "",
        okay: false,
      },
      evidence: [],
    }),
    [config.code, user],
  );

  const [mode, setMode] = useState<"report" | "status">("report");

  const [bulkEmail, setBulkEmail] = useState<string>("");

  const updateBulkEmail = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) =>
      setBulkEmail((event.target.value || "").trim()),
    [setBulkEmail],
  );

  const bulkEmailError = useMemo(() => {
    return !Boolean(
      bulkEmail.match(/^[^@]+@(?:[\w-]+\.)+[^\W_]{2,}$/) &&
      okayDomain(bulkEmail.replace(/^[^@]*@/, "")),
    );
  }, [bulkEmail]);

  const [bulkRequestOkay, setBulkRequestOkay] = useState(false);

  const clearBulkRequestOkay = useCallback(() => {
    setBulkRequestOkay(false);
    navigate(`/${config.code}`);
  }, [setBulkRequestOkay, navigate]);

  const requestBulkApi = useCallback(() => {
    if (bulkEmailError) {
      return;
    }
    callFunction("abuseForm_requestBulkApi", {
      email: bulkEmail,
      client: config.code,
    }).then(() => {
      setBulkRequestOkay(true);
    });
  }, [bulkEmail, bulkEmailError, callFunction, config.code]);

  const [receipt, setReceipt] = useState<Receipt>({
    okay: false,
    errors: [],
    _id: null,
  });
  const clearError = useCallback(
    () => setReceipt((r) => ({ ...r, errors: [] })),
    [],
  );

  const [errors, setErrors] = useImmer<Record<string, string | false>>({});
  const [report, setReport] = useImmer<AbuseReport>(initialReport);
  const hasErrors = useMemo(
    () =>
      Boolean(Object.values(errors).find(Boolean)) ||
      (config.code === "porkbun" &&
        report.abuse.type?.id === "dmca" &&
        (!dmcaHosting || dmcaHosting === "No")) ||
      (config.code === "unstoppabledomains" &&
        report.abuse.type?.id === "dmca-copyright" &&
        (!dmcaHosting || dmcaHosting === "No")),
    [errors, dmcaHosting, config.code, report.abuse.type?.id],
  );

  const resetReport = useCallback(() => {
    setReport((draft) => {
      return { ...initialReport, reporter: draft.reporter };
    });
    setReceipt({ okay: false, errors: [], _id: null });
    navigate(`/${config.code}`);
  }, [initialReport]);

  const setReportType = useCallback(
    (type: AbuseType) => {
      setReport((draft) => {
        draft.abuse.type = type;
      });
    },
    [setReport],
  );

  const submitReport = useCallback(async () => {
    cancelSubmission.current = false;
    setReceipt({ okay: false, errors: [], _id: null });

    if (hasErrors || !report.abuse.type) {
      setReceipt({
        okay: false,
        errors: [
          "Please correct the missing or malformed fields in your report",
        ],
        _id: null,
      });
      return;
    }

    const captcha = await new Promise((pass) => {
      // @ts-ignore
      window.grecaptcha.enterprise.ready(async () => {
        // @ts-ignore
        const token = await window.grecaptcha.enterprise.execute(
          "6Lcl2cAbAAAAAKwwFmX9syDSQmvtWVYdnNTzVjuF",
          { action: "submit_report" },
        );
        pass(token);
        // const result = await callFunction("abuseForm_verifyRecaptcha", { token, expectedAction: "submit_report" })
        //   .catch((error) => ({ success: false, response: error.toString() }));
        // pass(result);
      });
    });

    const errors: string[] = [];

    setSubmitting(true);

    const ip = await fetch("https://api.ipify.org")
      .then((r) => r.text())
      .catch(() => {
        // errors.push("Failed to look up your IP address");
        return "0.0.0.0";
      });

    if (errors.length) {
      setReceipt({
        okay: false,
        errors,
        _id: null,
      });
      setSubmitting(false);
      return;
    }

    const _id = new BSON.ObjectId();

    const hostname = fromUrl(report.actor.url) as string;
    const evidence: string[] = [];

    for (let i = 0; i < report.evidence.length; i++) {
      const body = report.evidence[i];
      const ext = body.name.replace(/.*\./, "");
      const key = `evidence/${hostname}-${_id}-${i}.${ext}`;

      await callFunction("abuseForm_getSignedUrl", { key, bucket })
        .then((putUrl: string) => {
          // console.log("Uploading evidence file to:", putUrl);
          return fetch(putUrl, {
            method: "PUT",
            body,
          });
        })
        .then(
          () =>
            // user!.functions.getSignedUrl({ key, bucket, operation: "getObject" })
            `https://storage.googleapis.com/${bucket}/${key}`,
        )
        .then((getUrl: string) => {
          evidence.push(getUrl);
        })
        .catch((error: Error) => {
          errors.push(`Upload failed for ${body.name}: ${error.toString()}`);
          console.warn(error.toString());
        });
    }

    if (errors.length) {
      setReceipt({
        okay: false,
        errors,
        _id: null,
      });
      setSubmitting(false);
      return;
    }

    const icannDomain = okayDomain(hostname, true)!;
    const pslDomain = okayDomain(hostname, false)!;

    const actors = new Map<string, { url: string; tld: string }>();

    for (const domain of [/*icannDomain,*/ pslDomain]) {
      const tld = domain.replace(/^[^.]+\./, "");
      actors.set(domain, { url: report.actor.url, tld });
    }

    // abort if timed out
    if (cancelSubmission.current) {
      setSubmitting(false);
      return;
    }

    const { okay, error } = await callFunction(
      "abuseForm_createClientAbuseReport",
      {
        reportData: {
          _id,
          ts: new Date(),
          realmId: user!.id,
          token: "n/a",
          client: {
            code: report.client,
            name: config.name,
          },
          abuse: {
            ongoing: true,
            class: report.abuse.type!.class,
            type: report.abuse.type!.type,
            subtype: report.abuse.type!.subType,
            // group (n/a)
            // type (type.id)
            // name (type.type)
            date: report.abuse.date,
            description: report.abuse.description,
            ...report.details,
          },
          reporter: {
            email: report.reporter.email.trim().toLocaleLowerCase(),
            name: (report.reporter.fullName || "").trim(),
            phone: (report.reporter.phoneNumber || "").trim(),
            ip,
          },
          actors: [...actors].map(([domain, data]) => ({ domain, ...data })),
          version: 2,
          source: window.location.href,
          submit: true,
          evidence,
          success: false,
          captcha,
        },
      },
    );

    setReceipt({
      okay,
      errors: error ? [error] : [],
      _id,
      noId:
        report.client === "unstoppabledomains" &&
        report.abuse.type?.id === "dmca-copyright",
    });
    setSubmitting(false);
  }, [user?.id, report, hasErrors, callFunction]);

  const wrappedSubmitReport = useCallback(
    () =>
      Promise.race([
        // this function sets cancelSubmission.current = false
        submitReport(),

        // this timeout sets cancelSubmission.current = true after 30 seconds
        new Promise((_, reject) =>
          setTimeout(() => {
            cancelSubmission.current = true; // don't actually submit if timeout occurs
            reject(new Error("Report submission timed out after 30 seconds"));
          }, 30000),
        ),
      ]).catch((error: Error) => {
        setReceipt({
          okay: false,
          errors: [error.message || error.toString()],
          _id: null,
        });
        setSubmitting(false);
      }),
    [submitReport],
  );

  const updateReporterName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.reporter.fullName = event.target.value;
      }),
    [setReport],
  );

  const updateEmail = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.reporter.email = (event.target.value || "").trim();
      }),
    [setReport],
  );

  const updateReporterPhone = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.reporter.phoneNumber = event.target.value;
      }),
    [setReport],
  );

  const updateUrl = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.actor.url = event.target.value;
      }),
    [setReport],
  );

  const updateDmcaHosting = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setDmcaHosting(event.target.value),
    [setDmcaHosting],
  );

  const updateCopyright = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.details.copyrightHolder = event.target.value;
      }),
    [setReport],
  );

  const updateCopyrightDescription = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) =>
      setReport((draft) => {
        draft.details.copyrightDescription = event.target.value;
      }),
    [setReport],
  );

  const updateDescription = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) =>
      setReport((draft) => {
        draft.abuse.description = event.target.value;
      }),
    [setReport],
  );

  const updateSignature = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.details.copyrightSignature = event.target.value;
      }),
    [setReport],
  );

  const updateTarget = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setReport((draft) => {
        draft.details.target = event.target.value;
      }),
    [setReport],
  );

  useEffect(() => {
    const type = report.abuse.type?.id;

    const email = report.reporter.email;
    const okayEmail = Boolean(
      email.match(/^[^@]+@(?:[\w-]+\.)+[^\W_]{2,}$/) &&
      okayDomain(email.replace(/^[^@]*@/, "")),
    );

    const actor = report.actor.url;
    // accept domains like perl.com (no protocol) as valid using isDomain utility
    const okayAbuseActor =
      okayActor(actor, type === "dmca-copyright" ? true : false) ||
      isDomain(actor);

    const date = report.abuse.date;
    const okayDate = Boolean(
      date && date instanceof Date && !isNaN(date.getTime()),
    );
    const futureDate = Boolean(
      date && date instanceof Date && date.getTime() > Date.now(),
    );

    const desc = report.abuse.description;
    const okayDesc = desc.length > 10;

    const target = report.details.target || "";
    const okayTarget = type !== "phishing" || target.length > 1;

    const headers = report.details.headers || "";
    const okayHeaders =
      (type !== "spam") ||
      headers.replace(/\s+/g, "").length >= 10;

    const body = report.details.body || "";
    const okayBody =
      (type !== "spam" && type !== "sms" && type !== "comment") ||
      body.replace(/\s+/g, "").length >= 10;

    const url = report.details.url || "";
    const okayUrl = type !== "comment" || okayActor(url);

    const domain = okayAbuseActor
      ? new URL(actor.replace(/^(?!https?:\/\/)/, "https://")).hostname
      : "";
    const ourDomain =
      config.type === "registry" && config.abuseForm.global.restrict
        ? Boolean(
            config.monitor.some((monitor) => domain.endsWith("." + monitor)),
          )
        : true;

    const evidence = report.evidence;
    const okayEvidence =
      report.abuse.type?.id === "csam" || evidence.length > 0;

    const fullName = report.reporter.fullName;
    const okayFullName = (fullName?.length ?? 0) > 1;

    const phoneNumber1 = phone(report.reporter.phoneNumber || "", {
      validateMobilePrefix: false,
    });
    const phoneNumber2 = phone("+" + report.reporter.phoneNumber || "", {
      validateMobilePrefix: false,
    });
    const okayPhoneNumber = phoneNumber1.isValid || phoneNumber2.isValid;

    const copyrightHolder = report.details.copyrightHolder;
    const okayCopyrightHolder = (copyrightHolder?.length ?? 0) > 1;

    const copyrightDescription = report.details.copyrightDescription;
    const okayCopyrightDescription = (copyrightDescription?.length ?? 0) > 10;

    const copyrightAcknowledgement = report.details.copyrightAcknowledgement;
    const okaycopyrightAcknowledgement = copyrightAcknowledgement === true;

    const copyrightSworn = report.details.copyrightSworn;
    const okayCopyrightSworn = copyrightSworn === true;

    const copyrightSignature = report.details.copyrightSignature;
    const okaySignature =
      Boolean(copyrightSignature) && fullName === copyrightSignature;

    const isDMCA =
      (config.code === "porkbun" && type === "dmca") ||
      (config.code === "unstoppabledomains" && type === "dmca-copyright");

    setErrors((draft) => {
      draft.email =
        !okayEmail && "This does not look like a valid email address";
      draft.actor =
        (!okayAbuseActor && "This does not look like a valid URL") ||
        (!ourDomain &&
          `This domain is outside the jurisdiction of ${config.name}.`);
      draft.date = !okayDate && "This does not look like a valid date";
      draft.date = futureDate && "This date is in the future";
      draft.md5 = Boolean(report.details.md5 && !/^[a-fA-F0-9]{32}$/.test(report.details.md5)) && "Invalid MD5 hash";
      draft.sha1 = Boolean(report.details.sha1 && !/^[a-fA-F0-9]{40}$/.test(report.details.sha1)) && "Invalid SHA1 hash";
      draft.headers =!okayHeaders && "Headers too short, 10 non-whitespace characters minimum";
      draft.body =
        !okayBody && "Body too short, 10 non-whitespace characters minimum";
      draft.description = !okayDesc && "This description is too short";
      draft.target =
        !okayTarget && "Phishing reports must have a target provided";
      draft.url = !okayUrl && "Comment spam reports must have a URL provided";
      draft.evidence = !okayEvidence && !isDMCA && "You must provide evidence";
      draft.fullName =
        !okayFullName && isDMCA && "You must provide your full name";
      draft.phoneNumber =
        !okayPhoneNumber &&
        isDMCA &&
        "This does not look like a valid phone number";
      draft.copyrightHolder =
        !okayCopyrightHolder &&
        isDMCA &&
        config.code === "porkbun" &&
        "The copyright holder name is missing";
      draft.copyrightDescription =
        !okayCopyrightDescription &&
        isDMCA &&
        config.code === "porkbun" &&
        "The copyright description is too short";
      draft.copyrightAcknowledgement =
        !okaycopyrightAcknowledgement &&
        isDMCA &&
        "You must acknowledge the 512(f) Acknowledgment, Good Faith Belief and Authority to Act";
      draft.copyrightSignature =
        !okaySignature && isDMCA && "Your signature must match your full name";
      draft.copyrightSworn =
        !okayCopyrightSworn &&
        isDMCA &&
        "You must swear that the information you are providing is accurate";
    });
  }, [report]);

  const params = useParams();
  const type = params.type as string | undefined;

  const phase = report.phase;
  const nextPhase = useCallback(() => {
    setReport((draft) => {
      draft.phase++;
    });
  }, [setReport]);

  useEffect(() => {
    const reportType = localAbuseTypes.find(({ id }) => id === type);
    if (!type || !reportType) {
      setReport(initialReport);
    } else {
      setReportType(reportType);
    }
  }, [type]);

  const theme = "light";
  const code = config.code;
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(true);
  const clearLogoError = useCallback(() => setLogoError(false), [setLogoError]);
  const exampleTld = useMemo(() => {
    if (config.type === "registry") {
      return config.monitor[0];
    } else {
      return "com";
    }
  }, [config.type, config.monitor]);

  const abuseGroups = useMemo<AbuseGroups[]>(() => {
    const groups: AbuseGroups[] = [
      ...(config.abuseForm.en.csamSeparate
        ? [
            {
              heading: "Child Sexual Abuse Materials (CSAM)",
              preText:
                "Examples of Child Sexual Abuse Materials (CSAM) include:",
              examples: [
                "Content that promotes, encourages, or engages in the sexual abuse and/or exploitation of children (CSAM)",
              ],
              postText: config.abuseForm.en.csam ? (
                ""
              ) : (
                <>
                  To report a website distributing Child Sexual Abuse Materials
                  (CSAM), please go directly to the{" "}
                  <Anchor
                    target="_blank"
                    href="https://www.iwf.org.uk/en/uk-report/"
                  >
                    Internet Watch Foundation
                  </Anchor>
                </>
              ),
              types: [config.abuseForm.en.csam ? "csam" : "nocsam"],
            },
            {
              heading: "Inappropriate Content",
              preText: "Examples of inappropriate content include:",
              examples: [
                <>Violent or disturbing imagery</>,
                `Content that displays personal information such as ${wording(config.code, "Social Security")} numbers or credit card numbers`,
              ].filter(Boolean),
              postText: "",
              types: ["violence2", "pii"].filter(Boolean),
            },
          ]
        : [
            {
              heading: "Inappropriate Content",
              preText: "Examples of inappropriate content include:",
              examples: [
                <>
                  Violent or disturbing imagery,{" "}
                  <b>
                    <i>EXCLUDING</i>
                  </b>{" "}
                  Child Sexual Abuse Materials (CSAM)
                </>,
                `Content that displays personal information such as ${wording(config.code, "Social Security")} numbers or credit card numbers`,
                config.abuseForm.en.csam
                  ? "Content that promotes, encourages, or engages in the sexual abuse and/or exploitation of children (CSAM)"
                  : false,
              ].filter(Boolean),
              postText: config.abuseForm.en.csam ? (
                ""
              ) : (
                <>
                  To report a website distributing Child Sexual Abuse Materials
                  (CSAM), please go directly to the{" "}
                  <Anchor
                    target="_blank"
                    href="https://www.iwf.org.uk/en/uk-report/"
                  >
                    Internet Watch Foundation
                  </Anchor>
                </>
              ),
              types: [
                config.abuseForm.en.csam ? "csam" : "nocsam",
                "violence",
                "pii",
              ].filter(Boolean),
            },
          ]),
      {
        heading: "Malware",
        preText: "Examples of malware include:",
        examples: [
          `Viruses`,
          `Malicious code`,
          // `Corrupt files`,
          `Any other material intended to compromise a user's computer`,
        ],
        postText: "",
        types: ["malware"],
      },
      {
        heading: "Phishing",
        preText:
          "Websites, emails, or messages requesting confidential personal information.",
        examples: [
          "Phishing is the use of fraudulent communication and/or a fraudulent website to steal personal information",
        ],
        postText: "",
        types: ["phishing"],
      },
      {
        heading: "Spam",
        preText: "Spam can include repeated, unsolicited:",
        examples: [
          "Emails",
          "Text messages (SMS)",
          "Comments on social media, blogs, or forums",
        ],
        postText:
          "If you are reporting an unsolicited online comment, report the website contained within the comment, not the website hosting the comment",
        types: ["spam", "sms", "comment"],
      },
      {
        heading: "Copyright and Trademark Claims",
        preText: "Report the misuse of copyrighted or trademarked material:",
        examples: [],
        postText:
          "If the material is being used within the context of phishing, you will be redirected to the phishing report form.",
        types: [],
      },
      {
        heading: "Copyright / DMCA",
        preText:
          "If you believe content hosted on our platform infringes your copyright, you may submit a DMCA takedown notice including:",
        examples: [
          "The URL(s) where the infringing content appears",
          "A description of the copyrighted work claimed to have been infringed",
          "Proof that you are the copyright owner or authorized to act on their behalf",
          "Your contact information",
        ],
        postText:
          "We can only take action on copyright complaints when the reported domain is registered with us or content hosted on our infrastructure. If the reported content is hosted by another provider, you may need to contact the appropriate hosting provider directly.",
        types: [],
      },
      {
        heading: "Other",
        preText:
          "If your report does not fit into any of the above categories, please select Other and provide a detailed description of the abuse you are reporting.",
        examples: [],
        postText: "",
        types: [],
      },
    ].filter(Boolean);

    // add "Botnet" to examples[] for Malware for I.D.
    if (config.code === "donuts") {
      const malwareGroup = groups.find((g) => g.heading === "Malware");
      if (malwareGroup && Array.isArray(malwareGroup.examples) && !malwareGroup.examples.includes("Botnets")) {
        malwareGroup.examples.unshift("Botnets");
      }

      const otherGroup = groups.find((g) => g.heading === "Other");
      if (otherGroup) {
        otherGroup.examples.push(
          "Pharming (website spoofing)",
          "Trademark Infringement",
          "Distributed Denial of Service (DDoS) attacks",
          "Other Acceptable Use Policy violations",
          "Other forms of abuse not listed above"
        );
      }
    }

    if (config.code === "nominet") {
      let g = groups.find((g) => g.heading === "Inappropriate Content")!;
      g.heading = "Unlawful content";
      g.preText = "Examples of unlawful content include:";
      g.examples = [
        "Child Sexual Abuse Materials (CSAM)",
        "Non-Consensual Intimate Images (NCII)",
      ];
      g.postText = "";
      g.types = ["nocsam", "noncii"];

      g = groups.find((g) => g.heading === "Spam")!;
      g.preText =
        "Spam - when serving as a delivery mechanism for other forms of abuse:";
      g.examples = ["Emails", "Text messages (SMS)"];
      g.postText =
        "Nominet will only action spam that is being used to propagate other forms of abuse.";
      g.types = ["spam", "sms"];

      groups.push({
        heading: "Fraud",
        preText: (
          <>
            Using deception to trick another internet user into giving money or
            information.
            <br />
            <br />
            Examples include:
          </>
        ),
        examples: ["Fake webshops", "Cryptocurrency scams", "Fake pharmacies"],
        postText: "",
        types: ["fraud"],
      });
    }

    // Add a threats and harassment button labeled “Threats or Harassment”

    if (config.abuseTypes) {
      console.log(config.abuseTypes);
      for (const type of config.abuseTypes) {
        const group = groups.find((g) => g.heading === type.heading) || groups.find((g) => g.heading === "Other")!;
        group.types.push(type.name);
        if (type.description) group.examples.push(type.description);

        setLocalAbuseTypes((prev) => [
          ...prev,
          {
            id: type.name,
            class: "Content",
            type: type.xarf,
            subType: type.subType,
            showType: type.showType,
            label: type.label,
          },
        ]);
      }
    }

    if (config.code === 'donuts') {
      // rename the "Malware" group & button so it explicitly mentions Botnet
      const malwareGroup = groups.find((g) => g.heading === "Malware");
      if (malwareGroup) {
        malwareGroup.heading = "Malware & Botnets";

        setLocalAbuseTypes((prev: any[]) => {
          return prev.map((item) =>
            item.id === "malware" ? { ...item, label: "Malicious software, virus, or botnet" } : item
          );
        });
      }
    }

    return groups.filter((g) => g.types.length > 0);
  }, [config.code, config.abuseForm, config.abuseTypes]);

  const [cached, setCached] = useState<{ [key: string]: boolean }>({});

  const fallbackLogo = useCallback(() => {
    setLogoError(true);
    if (logo.match(/_(light|dark)\.png$/)) {
      setLogo(logo.replace(/_(light|dark)\.png$/, ".png"));
    } else {
      setLogo("");
    }
  }, [theme, code, logo, setLogo]);

  useEffect(() => {
    setLogo(
      `https://storage.googleapis.com/cleandns_public_images_and_logos/logos/${code}_${theme}.png`,
    );
  }, [code, theme]);

  const handlePhishingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTrademarkOption(event.target.value);
    if (event.target.value === "Yes") {
      navigate("/porkbun/phishing");
    }
  };

  // handle iframe hash changes
  useEffect(() => {
    function handleHash() {
      const hash = window.location.hash.replace("#", "").toLowerCase();
      const idx = abuseGroups.findIndex((g) => toKebab(g.heading) === hash);
      setActiveIndex(idx !== -1 ? [idx] : []);
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, [abuseGroups]);

  if (!user) return <LoadingIndicator />;

  return (
    <Page kind="wide" responsive>
      {config.abuseForm.en.stickyHeader && !isEmbedded ? (
        <Header className="header">
          {logoError && (
            <Box justify="center">
              <Text>{config.name} Abuse Reporting Center</Text>
            </Box>
          )}
          {logo && (
            <Image
              src={logo}
              style={{ display: logoError ? "none" : "inline" }}
              height={config.abuseForm.theme?.headerLogoHeight || "48px"}
              alt={`${config.name} logo`}
              onLoad={clearLogoError}
              onError={fallbackLogo}
            />
          )}
          {!logoError && <Text>{wording(config.code, "Abuse Center")}</Text>}
          <Box flex />
          {config.code === "nominet" ? (
            <Anchor
              target="_blank"
              href="https://www.nominet.uk/privacy-notice/"
            >
              Privacy Policy
            </Anchor>
          ) : (
            ""
          )}
          <Button
            primary
            label={
              mode === "report" ? "Check Report Status" : "Submit a New Report"
            }
            onClick={() => setMode(mode === "report" ? "status" : "report")}
          />
        </Header>
      ) : (
        <Box direction="row" pad="small">
          <Box flex />
          {config.code === "nominet" ? (
            <Anchor
              target="_blank"
              href="https://www.nominet.uk/privacy-notice/"
            >
              Privacy Policy
            </Anchor>
          ) : (
            ""
          )}
          <Button
            primary
            label={
              mode === "report" ? "Check Report Status" : "Submit a New Report"
            }
            onClick={() => setMode(mode === "report" ? "status" : "report")}
          />
          <Box flex />
        </Box>
      )}
      {mode === "status" && <CheckStatus />}
      {mode === "report" && (
        <>
          {receipt.okay && receipt.noId && (
            <Layer onClickOutside={resetReport} onEsc={resetReport}>
              <Box pad="medium" width="medium" align="center" gap="small">
                <Text weight="bold">Thank you for your abuse report.</Text>
                <Text>
                  Unstoppable Domains will be in touch with you about your
                  report.
                </Text>
                <Button label="Submit Another Report" onClick={resetReport} />
              </Box>
            </Layer>
          )}
          {receipt.okay && !receipt.noId && receipt._id && (
            <Layer onClickOutside={resetReport} onEsc={resetReport}>
              <Box pad="medium" width="medium" align="center" gap="small">
                <Text weight="bold">Thank you for your abuse report.</Text>
                <Text>Retain this report ID for your records:</Text>
                <Text>{receipt._id.toString()}</Text>
                <Text>
                  You can use this report ID and your email address to get an
                  update on your abuse report by clicking the "Check Report
                  Status" button above.
                </Text>
                <NameValueList
                  pairProps={{ direction: "column" }}
                  gap="small"
                  margin={{ left: "large" }}
                >
                  <NameValuePair name={"Reported Domain:"}>
                    {report.actor.url}
                  </NameValuePair>
                  <NameValuePair name={"Time of Report:"}>
                    {new Date().toLocaleString().replace(/:\d\d /, " ")}
                  </NameValuePair>
                </NameValueList>
                <Button label="Submit Another Report" onClick={resetReport} />
              </Box>
            </Layer>
          )}
          {!receipt.okay && receipt.errors.length > 0 && (
            <Layer onClickOutside={clearError} onEsc={clearError}>
              <Box pad="medium" width="medium" align="center" gap="small">
                <Text weight="bold">
                  There{" "}
                  {receipt.errors.length === 1 ? "was an error" : "were errors"}{" "}
                  submitting your abuse report.
                </Text>
                {receipt.errors.map((error, i) => (
                  <Text key={i}>&bull; {error}</Text>
                ))}
                <Button label="Close and Try Again" onClick={clearError} />
              </Box>
            </Layer>
          )}
          {bulkRequestOkay && (
            <Layer
              onClickOutside={clearBulkRequestOkay}
              onEsc={clearBulkRequestOkay}
            >
              <Box pad="medium" width="medium" align="center" gap="small">
                <Text weight="bold">
                  Your request for bulk submission API credentials has been
                  forwarded on to our Abuse Partner CleanDNS.
                </Text>
                <Text>
                  A member of the CleanDNS team will reach out to begin the
                  onboarding process shortly.
                </Text>
                <Button label="Close" onClick={clearBulkRequestOkay} />
              </Box>
            </Layer>
          )}
          {!receipt.okay && !report.abuse.type && type !== "bulksignup" && (
            <>
              <PageContent gap="1rem" style={{ maxWidth: "900px" }}>
                {!config.abuseForm.en.stickyHeader && (
                  <Heading level="3" margin="none" style={{ maxWidth: "100%" }}>
                    <>
                      {config.abuseForm.en.title ||
                        `Submit an abuse report to ${config.name}`}
                    </>
                  </Heading>
                )}
                <Text size="large">
                  {config.code === "nominet" ? (
                    <>
                      We take all reports of abuse seriously and take
                      appropriate action based on the threat. We count abuse as
                      including; unlawful content, botnets, pharming, malware,
                      phishing, spam (that is being used to propagate other
                      forms of abuse) and fraud among others. Our Registry
                      Policies can be found{" "}
                      <Anchor
                        target="_blank"
                        href="https://nominet.uk/uk-registry/uk-policy/"
                      >
                        here
                      </Anchor>
                      .<br />
                      <br />
                      We will review abuse reports for any Top-Level Domains
                      where we are the Registry Operator – you can find the full
                      list of Nominet's TLDs{" "}
                      <Anchor
                        target="_blank"
                        href="https://registrars.nominet.uk/registry/"
                      >
                        here
                      </Anchor>
                      .
                    </>
                  ) : config.code === "keysystems" ? (
                    <>
                      Team Internet takes abuse reports very seriously and the
                      proper handling of your case is important to us. Please
                      use the form below to report any domain name primarily
                      engaged in abusive behavior registered through one of our
                      group registrars:
                      <ul
                        style={
                          {
                            // display: 'flex',
                            // justifyContent: 'center',
                            // alignItems: 'center',
                            // flexWrap: 'wrap',
                            // gap: '0.5rem 2rem',
                            // listStyle: 'none',
                            // padding: 0,
                            // margin: '0.5rem 0'
                          }
                        }
                      >
                        {config.abuseForm.partners!.map((partner) => (
                          <li key={partner}>{partner}</li>
                        ))}
                      </ul>
                      Abuse includes inappropriate content, malware, phishing,
                      and spam. If you are unsure if our abuse policy applies to
                      your complaint, feel free to refer to our{" "}
                      <TOSLink config={config.abuseForm} />.
                    </>
                  ) : (
                    <>
                      {config.name} takes abuse reports very seriously and the
                      proper handling of your case is important to us. Abuse
                      includes inappropriate content, malware, phishing, and
                      spam.
                      {Boolean(config.abuseForm.en.linkText) && (
                        <>
                          {" "}
                          If you are unsure if our abuse policy applies to your
                          complaint, feel free to refer to our{" "}
                          <TOSLink config={config.abuseForm} />.
                        </>
                      )}
                    </>
                  )}
                </Text>
                <Text size="large">
                  {config.code === "nominet" ? (
                    <>
                      Abuse will be reviewed based on information provided – so
                      please include any additional context or evidence that
                      would be of relevance to your case. Please provide your
                      email address, the full web address you would like to
                      report (http//www.exampledomain.uk/page5) and a detailed
                      description of the suggested abuse.{" "}
                    </>
                  ) : (
                    <>
                      Please provide your actual email address, the full URL of
                      the website you would like to report (example:{" "}
                      <Text as="kbd">{`https://www.exampledomain.${exampleTld}/page5`}</Text>
                      ), and a detailed description of the incident.{" "}
                    </>
                  )}
                </Text>
                <Text size="large">
                  {config.code === "nominet" ? (
                    <>
                      If you need to check the status of a previously submitted
                      report, please click the “Check Report Status” button. For
                      all other domain issues and questions – please contact
                      Nominet customer support.
                    </>
                  ) : (
                    <>
                      To check the status of a previously submitted abuse
                      report, please click the "Check Report Status" button
                      above. For all other domain issues and questions, please
                      reach out to <CustomerSupport config={config.abuseForm} />
                      .
                    </>
                  )}
                </Text>
                <Box direction="row" align="center" gap="small">
                  <StatusWarning color="status-critical" />
                  <Text size="large">
                    {config.code === "nominet" ? (
                      <>
                        It is important that you choose the correct abuse type
                        for your report. Miscategorised reports may be rejected
                        or delayed. If the type of abuse you wish to report is
                        not included on our form – please send an email to{" "}
                        <Anchor href="mailto:abuse@nominet.uk">
                          abuse@nominet.uk
                        </Anchor>
                      </>
                    ) : (
                      <>
                        Please ensure you choose the correct abuse type for your
                        report. Clearly miscategorized reports may result in the
                        rejection of your report (e.g. categorizing
                        trademark-related incidents as "Violent or Disturbing
                        Imagery").
                      </>
                    )}
                  </Text>
                </Box>
                {config.code === "nominet" && (
                  <Text size="large">
                    All reports will be reviewed – but may not result in
                    suspension or action being taken if not found to have been
                    abusive.
                  </Text>
                )}
                {config.code === "keysystems" && (
                  <Text size="large">
                    For law enforcement and government requests, please use our
                    dedicated law enforcement contact method the details of
                    which are available upon request.
                  </Text>
                )}
                <Accordion
                  margin={{ bottom: "large" }}
                  activeIndex={activeIndex}
                  onActive={setActiveIndex}
                >
                  {abuseGroups.map((group) => (
                    <AccordionPanel
                      key={group.heading}
                      label={
                        <Box direction="column">
                          <Heading
                            id={toKebab(group.heading)}
                            level={4}
                            margin={{ top: "small", bottom: "none" }}
                          >
                            {group.heading}
                          </Heading>
                          <Box
                            margin={{ top: "small", bottom: "small" }}
                            style={{ fontSize: "small" }}
                          >
                            {group.preText && (
                              <Text size="10pt">{group.preText}</Text>
                            )}
                            <ul>
                              {group.examples.map((ex, n) => (
                                <li key={n}>{ex}</li>
                              ))}
                            </ul>
                            {group.postText && (
                              <Text size="small">{group.postText}</Text>
                            )}
                          </Box>
                        </Box>
                      }
                    >
                      <Box
                        direction="row"
                        gap="small"
                        margin={{ bottom: "medium" }}
                        justify="center"
                      >
                        {group.types
                          .map((id) =>
                            localAbuseTypes.find((type) => type.id === id),
                          )
                          .filter(Boolean)
                          .map((type) => (
                            <Button
                              primary
                              key={type!.id}
                              label={type!.label}
                              icon={<Next color="white" size="small" />}
                              reverse
                              size="small"
                              onClick={() => {
                                if (type!.id === "nocsam") {
                                  window.open(
                                    "https://www.iwf.org.uk/en/uk-report/",
                                  );
                                } else if (type!.id === "noncii") {
                                  window.open(
                                    "https://swgfl.org.uk/helplines/stopncii/",
                                  );
                                } else {
                                  navigate(`/${config.code}/${type!.id}`);
                                }
                              }}
                            />
                          ))}
                      </Box>
                    </AccordionPanel>
                  ))}
                  {config.abuseForm.global.bulkBox ? (
                    <AccordionPanel
                      key={"bulkBox"}
                      label={
                        <Box direction="column">
                          <Heading
                            level={4}
                            margin={{ top: "small", bottom: "none" }}
                          >
                            Bulk Report Submission
                          </Heading>
                          <Box
                            margin={{ top: "small", bottom: "small" }}
                            style={{ fontSize: "small" }}
                          >
                            <Text size="10pt">
                              To submit reports in bulk, you will need to
                              provide your email address to our partner
                              CleanDNS.
                            </Text>
                          </Box>
                        </Box>
                      }
                    >
                      <Box
                        direction="row"
                        gap="small"
                        margin={{ bottom: "medium" }}
                        justify="center"
                      >
                        <Button
                          primary
                          label={"Request Access"}
                          icon={<Next color="white" size="small" />}
                          reverse
                          size="small"
                          onClick={() => navigate(`/${config.code}/bulksignup`)}
                        />
                      </Box>
                    </AccordionPanel>
                  ) : null}
                </Accordion>
              </PageContent>
            </>
          )}
          {type === "bulksignup" && (
            <Box
              width="large"
              direction="column"
              align="center"
              gap="small"
              flex
              margin="auto"
            >
              <Text>
                To submit reports in bulk, please enter your email address below
                to request API reporting credentials from our partner CleanDNS.
                This will sign you up as a trusted reporter with their service
                and allow you to submit API reports for any TLD using their
                service.
              </Text>
              <Box
                width="large"
                direction="row"
                align="center"
                gap="small"
                flex
                margin="auto"
              >
                <Box flex>
                  <FormElement
                    component={TextInput}
                    required
                    label="Your Email Address"
                    error={
                      bulkEmailError
                        ? "This does not look like a valid email address"
                        : false
                    }
                    name="email"
                    value={bulkEmail}
                    placeholder="e.g. me@email.com"
                    onChange={updateBulkEmail}
                  />
                </Box>
                <Button
                  primary
                  disabled={bulkEmailError}
                  onClick={requestBulkApi}
                  size="small"
                  label="Request Bulk API Access"
                />
              </Box>
            </Box>
          )}
          {report.abuse.type && (
            <>
              <PageContent gap="1rem" style={{ maxWidth: "900px" }}>
                {!config.abuseForm.en.stickyHeader && (
                  <Heading level="3" margin="none" style={{ maxWidth: "100%" }}>
                    <>
                      {config.abuseForm.en.title ||
                        `Submit an abuse report to ${config.name}`}
                    </>
                  </Heading>
                )}
                {report.abuse.type.id === "violence" && (
                  <Box direction="row" gap="small" align="center">
                    <StatusWarning color="status-critical" />
                    <Text size="large">
                      Reports of Child Sexual Abuse Materials (CSAM) MUST only
                      be reported via the CSAM option. Please DO NOT submit any
                      report of CSAM, nor upload any CSAM evidence, under the
                      category of "Violent or Disturbing Imagery", to ensure
                      these reports are being handled in accordance with
                      applicable law.
                    </Text>
                  </Box>
                )}
                <Box>
                  <Heading level={4} margin={{ top: "small", bottom: "none" }}>
                    Report {report.abuse.type.showType || report.abuse.type.type} Abuse
                  </Heading>
                </Box>
                {config.code === "unstoppabledomains" &&
                  report.abuse.type.id === "dmca-copyright" && (
                    <>
                      <FormElement
                        component={TextInput}
                        required
                        label="Full Name"
                        help="Enter your full name."
                        error={errors.fullName}
                        name="fullName"
                        value={report.reporter.fullName}
                        placeholder="e.g. John Doe"
                        onChange={updateReporterName}
                      />
                      <FormElement
                        component={TextInput}
                        required
                        label="Email Address"
                        help="Enter your email address."
                        error={errors.email}
                        name="email"
                        value={report.reporter.email}
                        placeholder="e.g. me@email.com"
                        onChange={updateEmail}
                      />
                      <FormElement
                        component={TextInput}
                        required
                        label="Phone Number"
                        help="Enter your phone number."
                        error={errors.phoneNumber}
                        name="phoneNumber"
                        value={report.reporter.phoneNumber}
                        placeholder="e.g. 1234567890"
                        onChange={updateReporterPhone}
                      />
                      <FormElement
                        component={RadioButtonGroup}
                        required
                        label="While the domain may be registered at Unstoppable Domains, it could be using web hosting services from a third party. Is this domain using Unstoppable Domains’ web hosting services?"
                        help="Please select an option."
                        name="dmcaHosting"
                        value={dmcaHosting}
                        error={errors.dmcaHosting}
                        options={dmcaHostingRadioOptions}
                        onChange={updateDmcaHosting}
                        conditionalText={
                          dmcaHosting === "No" ? (
                            <Text>
                              If you are looking to file a notice of copyright
                              infringement via the method detailed in the
                              Digital Millennium Copyright Act, our
                              understanding is that the terms of the DMCA apply
                              to web hosting companies and ISPs, not registrars.
                              Furthermore, we don't have the ability to remove
                              specific content from the site. In order to have
                              any content removed, you should contact the
                              registrant, and/or the domain's web hosting
                              company.
                            </Text>
                          ) : undefined
                        }
                      />
                    </>
                  )}
                {config.code === "porkbun" &&
                  report.abuse.type.id === "dmca" && (
                    <>
                      <FormElement
                        component={TextInput}
                        required
                        label="Full Name"
                        help="Enter your full name."
                        error={errors.fullName}
                        name="fullName"
                        value={report.reporter.fullName}
                        placeholder="e.g. John Doe"
                        onChange={updateReporterName}
                      />
                      <FormElement
                        component={TextInput}
                        required
                        label="Email Address"
                        help="Enter your email address."
                        error={errors.email}
                        name="email"
                        value={report.reporter.email}
                        placeholder="e.g. me@email.com"
                        onChange={updateEmail}
                      />
                      <FormElement
                        component={TextInput}
                        required
                        label="Phone Number"
                        help="Enter your phone number."
                        error={errors.phoneNumber}
                        name="phoneNumber"
                        value={report.reporter.phoneNumber}
                        placeholder="e.g. 1234567890"
                        onChange={updateReporterPhone}
                      />
                      <FormElement
                        component={RadioButtonGroup}
                        required
                        label="While the domain may be registered at Porkbun, it could be using web hosting services from a third party. Is this domain using Porkbun’s web hosting services?"
                        help="Please select an option."
                        name="porkbunHosting"
                        value={dmcaHosting}
                        error={errors.dmcaHosting}
                        options={dmcaHostingRadioOptions}
                        onChange={updateDmcaHosting}
                        conditionalText={
                          dmcaHosting === "No" ? (
                            <Text>
                              If you are looking to file a notice of copyright
                              infringement via the method detailed in the
                              Digital Millennium Copyright Act, our
                              understanding is that the terms of the DMCA apply
                              to web hosting companies and ISPs, not registrars.
                              Furthermore, we don't have the ability to remove
                              specific content from the site. In order to have
                              any content removed, you should contact the
                              registrant, and/or the domain's web hosting
                              company.
                            </Text>
                          ) : undefined
                        }
                      />
                    </>
                  )}
                {config.code === "porkbun" &&
                  report.abuse.type.id === "trademark" && (
                    <>
                      {/* <FormField>
                      <Box pad="small">
                        <Text>
                          If the trademark complaint is related to the content of the domain, please use the <Link to="../dmca">DMCA/Copyright form</Link>.
                        </Text>
                      </Box>
                    </FormField> */}
                      <Box>
                        <FormElement
                          component={RadioButtonGroup}
                          required
                          label="Is this domain engaged in phishing?"
                          help="Please select an option."
                          name="trademark"
                          value={phishingOption}
                          options={phishingRadioOptions}
                          onChange={handlePhishingChange}
                          conditionalText={
                            phishingOption === "No" ? (
                              <Text>
                                Please note that Porkbun is not a trademark
                                authority and cannot provide legal advice or
                                adjudicate trademark disputes. We encourage
                                anyone with concerns about trademarks to seek
                                guidance from a qualified professional.
                                <br />
                                <br />
                                That said, ICANN rules as well as Porkbun's own
                                policies prohibit us from arbitrating trademark
                                disputes in domain names. There are, however, a
                                few remedies we are aware of, including but not
                                limited to the{" "}
                                <a href="https://www.icann.org/resources/pages/help/dndr/udrp-en">
                                  UDRP
                                </a>{" "}
                                and the{" "}
                                <a href="https://www.icann.org/resources/pages/urs-2014-01-09-en">
                                  URS
                                </a>
                                .
                                <br />
                                <br />
                                If you'd like to contact the registrant
                                directly, we provide a method. Here is our guide
                                on{" "}
                                <a href="https://kb.porkbun.com/article/154-how-to-contact-a-porkbun-registrant">
                                  how to contact a Porkbun registrant
                                </a>
                                .
                                <br />
                                <br />
                                If the trademark complaint is related to the
                                content of the domain, please use the{" "}
                                <Link to="../dmca">DMCA/Copyright form</Link>.
                              </Text>
                            ) : undefined
                          }
                        />
                      </Box>
                    </>
                  )}
                {!(
                  config.code === "porkbun" &&
                  (report.abuse.type.id === "trademark" ||
                    report.abuse.type.id === "dmca")
                ) &&
                  !(
                    config.code === "unstoppabledomains" &&
                    report.abuse.type.id === "dmca-copyright"
                  ) && (
                    <FormElement
                      component={TextInput}
                      required
                      label="Your Email Address"
                      help={
                        <>
                          Submitting <b>your</b> email address is{" "}
                          <b>necessary</b> for your abuse report to be
                          processed.
                        </>
                      }
                      error={errors.email}
                      name="email"
                      value={report.reporter.email}
                      placeholder="e.g. me@email.com"
                      onChange={updateEmail}
                    />
                  )}
                {phase === 1 && (
                  <>
                    <Box
                      direction="row"
                      gap="small"
                      margin={{ bottom: "medium" }}
                      justify="between"
                    >
                      <Button
                        color="status-critical"
                        onClick={resetReport}
                        size="small"
                        label={<Text color="black">Cancel</Text>}
                      />

                      <Button
                        primary
                        disabled={
                          config.code === "porkbun" &&
                          report.abuse.type.id === "dmca"
                            ? Boolean(
                                errors.fullName ||
                                errors.email ||
                                errors.phoneNumber,
                              ) ||
                              !dmcaHosting ||
                              dmcaHosting === "No"
                            : config.code === "unstoppabledomains" &&
                                report.abuse.type.id === "dmca-copyright"
                              ? Boolean(
                                  errors.fullName ||
                                  errors.email ||
                                  errors.phoneNumber,
                                ) ||
                                !dmcaHosting ||
                                dmcaHosting === "No"
                              : Boolean(errors.email)
                        }
                        onClick={nextPhase}
                        size="small"
                        label="Continue"
                      />
                    </Box>
                  </>
                )}
                {phase > 1 &&
                  config.code === "unstoppabledomains" &&
                  report.abuse.type.id === "dmca-copyright" && (
                    <>
                      <Box>
                        <Heading
                          level={4}
                          margin={{ top: "small", bottom: "none" }}
                        >
                          General Details
                        </Heading>
                      </Box>
                      <FormElement
                        component={TextArea}
                        required
                        label="Infringing URL(s)"
                        help="Enter the complete URL(s) hosting the abusive content, one per line."
                        error={errors.actor}
                        name="actor"
                        value={report.actor.url}
                        placeholder={`e.g. https://www.exampledomain.${exampleTld}/page5`}
                        onChange={updateUrl}
                      />
                      {/* <FormElement
                      component={TextInput}
                      required
                      label="Copyright Holder Information"
                      help="Information about the legal copyright holder."
                      error={errors.copyrightHolder}
                      name="copyright"
                      value={report.details.copyrightHolder}
                      onChange={updateCopyright}
                    /> */}
                      <FormElement
                        component={TextArea}
                        error={errors.description}
                        required
                        label="Description of Copyrighted Work"
                        help="Describe the copyrighted work in full detail, including links to the original work or source material."
                        name="description"
                        value={report.abuse.description}
                        rows={3}
                        onChange={updateDescription}
                      />
                      {/* <FormElement
                      component={TextArea}
                      error={errors.description}
                      label="Additional Information"
                      name="description"
                      value={report.abuse.description}
                      rows={3}
                      placeholder="e.g. This website asked for my credit card number."
                      onChange={updateDescription}
                    /> */}
                      {phase === 2 && (
                        <>
                          <Box
                            direction="row"
                            gap="small"
                            margin={{ bottom: "medium" }}
                            justify="between"
                          >
                            <Button
                              color="status-critical"
                              onClick={resetReport}
                              size="small"
                              label="Cancel"
                            />
                            <Button
                              primary
                              disabled={Boolean(
                                errors.actor ||
                                Boolean(
                                  errors.fullName ||
                                  errors.email ||
                                  errors.phoneNumber ||
                                  errors.description,
                                ) ||
                                !dmcaHosting ||
                                dmcaHosting === "No",
                              )}
                              onClick={nextPhase}
                              size="small"
                              label="Continue"
                            />
                          </Box>
                        </>
                      )}
                    </>
                  )}

                {phase > 1 &&
                  config.code === "porkbun" &&
                  report.abuse.type.id === "dmca" && (
                    <>
                      <Box>
                        <Heading
                          level={4}
                          margin={{ top: "small", bottom: "none" }}
                        >
                          General Details
                        </Heading>
                      </Box>
                      <FormElement
                        component={TextInput}
                        required
                        label="Copyright Holder Information"
                        help="Information about the legal copyright holder."
                        error={errors.copyrightHolder}
                        name="copyright"
                        value={report.details.copyrightHolder}
                        onChange={updateCopyright}
                      />
                      <FormElement
                        component={TextInput}
                        required
                        label="Infringing URL"
                        help="Enter the complete URL hosting the abusive content."
                        error={errors.actor}
                        name="actor"
                        value={report.actor.url}
                        placeholder={`e.g. https://www.exampledomain.${exampleTld}/page5`}
                        onChange={updateUrl}
                      />
                      <FormElement
                        component={TextArea}
                        error={errors.copyrightDescription}
                        label="Description of Original Work"
                        help="Describe the copyrighted work in full detail, including links to the original work or source material."
                        name="description"
                        value={report.details.copyrightDescription}
                        rows={3}
                        onChange={updateCopyrightDescription}
                      />
                      <FormElement
                        component={TextArea}
                        required
                        error={errors.description}
                        label="Additional Information"
                        name="description"
                        value={report.abuse.description}
                        rows={3}
                        placeholder="e.g. This website asked for my credit card number."
                        onChange={updateDescription}
                      />
                      {phase === 2 && (
                        <>
                          <Box
                            direction="row"
                            gap="small"
                            margin={{ bottom: "medium" }}
                            justify="between"
                          >
                            <Button
                              color="status-critical"
                              onClick={resetReport}
                              size="small"
                              label="Cancel"
                            />
                            <Button
                              primary
                              disabled={Boolean(
                                errors.actor ||
                                errors.copyrightHolder ||
                                Boolean(
                                  errors.fullName ||
                                  errors.email ||
                                  errors.phoneNumber ||
                                  errors.description,
                                ) ||
                                !dmcaHosting ||
                                dmcaHosting === "No",
                              )}
                              onClick={nextPhase}
                              size="small"
                              label="Continue"
                            />
                          </Box>
                        </>
                      )}
                    </>
                  )}
                {phase > 1 &&
                  !(
                    config.code === "porkbun" &&
                    (report.abuse.type.id === "trademark" ||
                      report.abuse.type.id === "dmca")
                  ) &&
                  !(
                    config.code === "unstoppabledomains" &&
                    report.abuse.type.id === "dmca-copyright"
                  ) && (
                    <>
                      <Box>
                        <Heading
                          level={4}
                          margin={{ top: "small", bottom: "none" }}
                        >
                          General Details
                        </Heading>
                      </Box>
                      <FormElement
                        component={TextInput}
                        required
                        label="Offending URL"
                        help="Enter the complete URL hosting the abusive content."
                        error={errors.actor}
                        name="actor"
                        value={report.actor.url}
                        placeholder={`e.g. https://www.exampledomain.${exampleTld}/page5`}
                        onChange={updateUrl}
                        onBlur={async (ev) => {
                          setSubmitting(true);
                          let url = ev.target.value.trim();
                          if (url && !/^https?:\/\//i.test(url)) {
                            try {
                              const res = await fetch(
                                "https://us-central1-dot-avoid.cloudfunctions.net/detectProtocol",
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({ domain: url }),
                                },
                              );
                              const data = await res.json();
                              // console.log({ url, now: ev.target.value.trim(), data: data.url })
                              if (data.url) {
                                setReport((draft) => {
                                  if (
                                    draft.actor.url === ev.target.value.trim()
                                  )
                                    draft.actor.url = data.url;
                                });
                              } else {
                                setReport((draft) => {
                                  if (
                                    draft.actor.url === ev.target.value.trim()
                                  )
                                    draft.actor.url = `http://${url}`;
                                });
                              }
                            } catch (err) {
                              // if error, assume http
                              setReport((draft) => {
                                if (draft.actor.url === ev.target.value.trim())
                                  draft.actor.url = `http://${url}`;
                              });
                            }
                          }
                          setSubmitting(false);
                        }}
                      />
                      <Box>
                        <FormField
                          required
                          label={required("Date of Abuse")}
                          error={errors.date}
                          help={
                            <small>
                              <i>
                                When was the abusive content found? ({dateInputFormat.toLocaleUpperCase()})
                              </i>
                            </small>
                          }
                        >
                          <DateInput
                            size="small"
                            required
                            // readOnly
                            name="date"
                            format={dateInputFormat}
                            max={new Date().toISOString()}
                            value={new Date(report.abuse.date).toISOString()}
                            onChange={(ev) => {
                              const newDate = new Date (ev.value as string);
                              if (newDate < new Date()) {
                                setReport((draft) => {
                                  draft.abuse.date = newDate;
                                })
                              }
                            }}
                          />
                        </FormField>
                      </Box>
                      <FormElement
                        component={TextArea}
                        required
                        label="Description of the Abusive Activity"
                        error={errors.description}
                        name="description"
                        value={report.abuse.description}
                        rows={3}
                        placeholder="e.g. This website asked for my credit card number."
                        onChange={updateDescription}
                      />
                      {phase === 2 && (
                        <>
                          <Box
                            direction="row"
                            gap="small"
                            margin={{ bottom: "medium" }}
                            justify="between"
                          >
                            <Button
                              color="status-critical"
                              onClick={resetReport}
                              size="small"
                              label="Cancel"
                            />
                            <Button
                              primary
                              disabled={Boolean(
                                errors.actor ||
                                errors.date ||
                                errors.description,
                              )}
                              onClick={nextPhase}
                              size="small"
                              label="Continue"
                            />
                          </Box>
                        </>
                      )}
                    </>
                  )}
                {phase > 2 &&
                  !(
                    config.code === "porkbun" &&
                    report.abuse.type.id === "trademark"
                  ) && (
                    <>
                      <Box>
                        <Heading
                          level={4}
                          margin={{ top: "small", bottom: "none" }}
                        >
                          Specific {report.abuse.type.type} Details
                        </Heading>
                      </Box>
                      {((config.code === "porkbun" &&
                        report.abuse.type.id === "dmca") ||
                        (config.code === "unstoppabledomains" &&
                          report.abuse.type.id === "dmca-copyright")) && (
                        <>
                          <FormField
                            required
                            label={required(
                              "512(f) Acknowledgment, Good Faith Belief and Authority to Act",
                            )}
                            help={
                              <Text size="small" color="#4a4a4a">
                                By checking this box, you attest, under penalty
                                of perjury, that you have a good faith belief
                                that use of the material in this report is not
                                authorized by the copyright owner, its agent, or
                                the law; AND you are authorized to act on behalf
                                of the copyright owner; AND you understand,
                                under 17 U.S.C. § 512(f), you may be liable for
                                any damages, including costs and attorneys'
                                fees, if you knowingly materially misrepresent
                                reported material.
                              </Text>
                            }
                            error={errors.copyrightAcknowledgement}
                          >
                            <CheckBox
                              label="I understand and agree"
                              checked={report.details.copyrightAcknowledgement}
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.copyrightAcknowledgement =
                                    event.target.checked;
                                })
                              }
                            />
                          </FormField>
                          <FormElement
                            component={TextInput}
                            required
                            label="Digital Signature"
                            help={
                              <Text size="small" color="#4a4a4a">
                                By entering your name, you affirm all
                                information is true and accurate while agreeing
                                to the policies on this page.
                              </Text>
                            }
                            error={errors.copyrightSignature}
                            name="signature"
                            value={report.details.copyrightSignature}
                            placeholder="e.g. John Doe"
                            onChange={updateSignature}
                          />
                          <FormField
                            required
                            label={required("Sworn Statement")}
                            help={
                              <Text size="small" color="#4a4a4a">
                                By continuing you declare under penalty of
                                perjury under the laws of{" "}
                                {config.code === "porkbun" && (
                                  <>
                                    the State of Oregon in the United States of
                                    America and{" "}
                                  </>
                                )}
                                your own legal jurisdiction that the information
                                being submitted is true and correct.
                              </Text>
                            }
                            error={errors.copyrightSworn}
                          >
                            <CheckBox
                              label="I understand and agree"
                              checked={report.details.copyrightSworn}
                              onChange={(event) =>
                                setReport((draft) => {
                                  draft.details.copyrightSworn =
                                    event.target.checked;
                                })
                              }
                            />
                          </FormField>
                        </>
                      )}
                      {(report.abuse.type.id === "phishing" ||
                        report.abuse.type.id === "fraud") && (
                        <>
                          {report.abuse.type.id === "phishing" && (
                            <FormElement
                              component={TextInput}
                              required
                              label="Brand or Company Being Imitated"
                              error={errors.target}
                              name="target"
                              value={report.details.target || ""}
                              placeholder="e.g. Amazon"
                              onChange={updateTarget}
                            />
                          )}
                          <Box>
                            <FormField
                              label={<b>Email Message Headers</b>}
                              help={
                                <small>
                                  <i>
                                    If you received a phishing email and can
                                    find the message headers, paste them here.
                                  </i>
                                </small>
                              }
                            >
                              <TextArea
                                name="headers"
                                size="small"
                                value={report.details.headers || ""}
                                // placeholder="e.g. Amazon"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.headers = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              label={<b>Email Message Body</b>}
                              help={
                                <small>
                                  <i>
                                    If you received a phishing email, paste the
                                    message body here.
                                  </i>
                                </small>
                              }
                            >
                              <TextArea
                                name="body"
                                size="small"
                                value={report.details.body || ""}
                                // placeholder="e.g. Amazon"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.body = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                        </>
                      )}
                      {report.abuse.type.id === "malware" && (
                        <>
                          <Box>
                            <FormField
                              label={<b>Malware Filename</b>}
                              help={
                                <small>
                                  <i>
                                    If you know the filename of the malware,
                                    provide it here.
                                  </i>
                                </small>
                              }
                            >
                              <TextInput
                                size="small"
                                name="filename"
                                value={report.details.filename || ""}
                                placeholder="e.g. cashappp.exe"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.filename = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              label={<b>Malware or Virus Name</b>}
                              help={
                                <small>
                                  <i>
                                    If you know the technical designation of the
                                    malware or virus, provide it here.
                                  </i>
                                </small>
                              }
                            >
                              <TextInput
                                size="small"
                                name="target"
                                value={report.details.malware || ""}
                                placeholder="e.g. TROJ_CRIBIT.B"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.malware = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              label={<b>MD5 Hash</b>}
                              validate={report.details.md5 && !/^[a-fA-F0-9]{32}$/.test(report.details.md5) ? "Invalid MD5 hash" : undefined}
                              help={
                                <small>
                                  <i>
                                    If you know the MD5 checksum of the malware,
                                    provide it here.
                                  </i>
                                </small>
                              }
                            >
                              <TextInput
                                size="small"
                                name="md5"
                                value={report.details.md5 || ""}
                                placeholder="e.g. 7215ee9c7d9dc229d2921a40e899ec5f (32 hexademical characters)"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.md5 = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              label={<b>SHA1 Hash</b>}
                              validate={report.details.sha1 && !/^[a-fA-F0-9]{40}$/.test(report.details.sha1) ? "Invalid SHA1 hash" : undefined}
                              help={
                                <small>
                                  <i>
                                    If you know the SHA1 checksum of the
                                    malware, provide it here.
                                  </i>
                                </small>
                              }
                            >
                              <TextInput
                                size="small"
                                name="target"
                                value={report.details.sha1 || ""}
                                placeholder="e.g. b858cb282617fb0956d960215c8e84d1ccf909c6 (40 hexademical characters)"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.sha1 = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                        </>
                      )}
                      {report.abuse.type.id === "spam" && (
                        <>
                          <Box>
                            <FormField
                              label={<b>Email Message Headers</b>}
                              validate={report.details.headers && report.details.headers.replace(/\s+/g, '').length < 10 ? "Headers too short, 10 non-whitespace characters minimum" : undefined}
                              help={
                                <small>
                                  <i>
                                    If you can find the message headers, paste
                                    them here.
                                  </i>
                                </small>
                              }
                            >
                              <TextArea
                                size="small"
                                name="headers"
                                value={report.details.headers || ""}
                                // placeholder="e.g. Amazon"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.headers = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              required
                              label={required("Email Message Body")}
                              validate={report.details.body && report.details.body.replace(/\s+/g, '').length < 10 ? "Body too short, 10 non-whitespace characters minimum" : undefined}
                              help={
                                <small>
                                  <i>Paste the message body here.</i>
                                </small>
                              }
                            >
                              <TextArea
                                required
                                size="small"
                                name="body"
                                value={report.details.body || ""}
                                // placeholder="e.g. Amazon"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.body = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                        </>
                      )}
                      {report.abuse.type.id === "sms" && (
                        <>
                          <Box>
                            <FormField label={<b>Sender of SMS</b>}>
                              <TextInput
                                size="small"
                                name="target"
                                value={report.details.smsNumber || ""}
                                placeholder="e.g. 1-212-555-1234"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.smsNumber =
                                      event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              required
                              label={required("SMS Message")}
                              help={
                                <small>
                                  <i>Paste the message body here.</i>
                                </small>
                              }
                            >
                              <TextArea
                                required
                                size="small"
                                name="body"
                                value={report.details.body || ""}
                                // placeholder="e.g. Amazon"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.body = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                        </>
                      )}
                      {report.abuse.type.id === "comment" && (
                        <>
                          <Box>
                            <FormField
                              required
                              label={required("URL of Unsolicited Comment")}
                              help={
                                <small>
                                  <i>
                                    This is the website where the comment
                                    appeared.
                                  </i>
                                </small>
                              }
                            >
                              <TextInput
                                required
                                size="small"
                                name="url"
                                value={report.details.url || ""}
                                placeholder="e.g. https://twitter.com/CheapGold/status/1283918203981203"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.url = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                          <Box>
                            <FormField
                              required
                              label={required("Message Body")}
                              help={
                                <small>
                                  <i>Paste the message body here.</i>
                                </small>
                              }
                            >
                              <TextArea
                                required
                                size="small"
                                name="body"
                                value={report.details.body || ""}
                                // placeholder="e.g. Amazon"
                                onChange={(event) =>
                                  setReport((draft) => {
                                    draft.details.body = event.target.value;
                                  })
                                }
                              />
                            </FormField>
                          </Box>
                        </>
                      )}
                      {report.abuse.type.id === "csam" ? (
                        <Box margin="1rem">
                          <Text>
                            Due to the sensitive nature of CSAM content, no
                            screenshots or other evidence is permitted to be
                            uploaded with your abuse report.
                          </Text>
                        </Box>
                      ) : (config.code === "porkbun" &&
                          report.abuse.type.id === "dmca") ||
                        (config.code === "unstoppabledomains" &&
                          report.abuse.type.id === "dmca-copyright") ? null : (
                        <Box>
                          <FormField
                            label={<b>Evidence of the Abusive Activity</b>}
                            error={errors.evidence}
                            help={
                              <small>
                                <div>
                                  <>Attach screenshots of the abusive website or emails to verify your report. </>
                                  {config.code === 'donuts' && <>
                                  Screenshots in PNG/JPG/JPEG/GIF can be attached to verify the report.
                                  Additional supporting evidence can be submitted in formats such as PDF,
                                  but may require further screening/processing and therefore delay review.  
                                  </>}
                                </div>
                              </small>
                            }
                          >
                            <FileInput
                              name="evidence"
                              maxSize={1024 * 1024 * 32} // 32 MB
                              multiple
                              onChange={(event, { files }) => {
                                (async function () {
                                  const images = (files || []) as File[];
                                  const acceptable: File[] = [];

                                  for (const image of images) {
                                    if (cached[image.name] === true) {
                                      console.log(`already approved ${image.name}`)
                                      acceptable.push(image);
                                      continue;
                                    }
                                    else if (cached[image.name] === false) {
                                      console.log(`already rejected ${image.name}`)
                                      continue;
                                    }
                                    const base64 = await fileToBase64(image);
                                    const response = await callFunction(
                                      "abuseForm_checkImage",
                                      { image: base64 },
                                    );

                                    if (
                                      response.adult >= 3 ||
                                      response.racy >= 4
                                    ) {
                                      console.log(`rejected ${image.name}`)
                                      setCached((cache) => ({ ...cache, [image.name]: false }))
                                      alert(
                                        `The image ${image.name} appears to contains adult content and is not acceptable for upload.`,
                                      );
                                    } else {
                                      console.log(`approved ${image.name}`)
                                      setCached((cache) => ({ ...cache, [image.name]: true }));
                                      acceptable.push(image);
                                    }
                                  }

                                  setReport((draft) => {
                                    // if (acceptable) {
                                    draft.evidence = acceptable;
                                    // [
                                    //   ...draft.evidence,
                                    //   ...acceptable,
                                    // ];
                                    // }
                                  });
                                })();
                              }}
                            />
                          </FormField>
                        </Box>
                      )}
                      <Box
                        direction="row"
                        gap="small"
                        margin={{ bottom: "medium" }}
                        justify="between"
                      >
                        <Button
                          color="status-critical"
                          onClick={resetReport}
                          size="small"
                          label="Cancel"
                        />
                        <Button
                          primary
                          disabled={hasErrors}
                          busy={submitting}
                          onClick={wrappedSubmitReport}
                          size="small"
                          label="Submit"
                        />
                      </Box>
                    </>
                  )}
              </PageContent>
            </>
          )}
        </>
      )}
      {/* <Text as="pre">{JSON.stringify(report, null, 4)}</Text> */}
      {!config.abuseForm.en.whiteLabeled && (
        <Box flex direction="row" justify="around">
          <Text margin="1rem">
            powered by{" "}
            <Anchor target="_blank" href="https://cleandns.com">
              CleanDNS
            </Anchor>
          </Text>
        </Box>
      )}
    </Page>
  );
}

function CustomerSupport({ config }: { config: AbuseFormConfig }) {
  const langs = Object.keys(config).filter((lang) => lang.length === 2);
  const [lang] = langs;
  if (langs.length === 1) {
    return (
      <Anchor target="_blank" href={config[lang].support}>
        customer support
      </Anchor>
    );
  } else {
    return (
      <>
        customer support{" "}
        {langs.map((lang) => (
          <React.Fragment key="lang">
            {" "}
            <Anchor target="_blank" key={lang} href={config[lang].support}>
              <FlagEmoji lang={lang} />
            </Anchor>
          </React.Fragment>
        ))}
      </>
    );
  }
}

function TOSLink({
  config,
  text = "",
}: {
  config: AbuseFormConfig;
  text?: string;
}) {
  const langs = Object.keys(config).filter((lang) => lang.length === 2);
  const [lang] = langs;
  if (langs.length === 1) {
    return (
      <Anchor target="_blank" href={config[lang].link}>
        {text || config[lang].linkText}
      </Anchor>
    );
  } else {
    return (
      <>
        {config[lang].linkText}{" "}
        {langs.map((lang) => (
          <React.Fragment key="lang">
            {" "}
            <Anchor target="_blank" key={lang} href={config[lang].link}>
              <FlagEmoji lang={lang} />
            </Anchor>
          </React.Fragment>
        ))}
      </>
    );
  }
}

function FlagEmoji({ lang }: { lang: string }) {
  return (
    <Image
      style={{ height: "1rem" }}
      title={`${lang} flag`}
      alt={`[${lang}]`}
      src={`https://storage.googleapis.com/cleandns_public_images_and_logos/flags/${lang}.png`}
    />
  );
}

async function fileToBase64(file: File) {
  return new Promise((pass, fail) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = () =>
      pass(
        fr.result?.toString().replace(/^data:image\/[^;]+;base64,/, "") || "",
      );
    fr.onerror = (error) => fail(error);
  });
}

function wording(client: string, word: string) {
  if (client === "nominet") {
    if (word === "Abuse Center") return "Abuse Centre";
    if (word === "categorized") return "categorised";
    if (word === "categorizing") return "categorising";
    if (word === "Social Security") return "National Insurance";
  }

  return word;
}

function required(label: string) {
  return (
    <b>
      {label} <Text color="status-critical">*</Text>
    </b>
  );
}


function getDateInputFormat() {
  const parts = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const order = parts
    .filter(part => ['year', 'month', 'day'].includes(part.type))
    .map(part => part.type);

  if (order.join('-') === 'month-day-year') {
    return 'mm/dd/yyyy';
  }

  if (order.join('-') === 'day-month-year') {
    return 'dd/mm/yyyy';
  }

  if (order.join('-') === 'year-month-day') {
    return 'yyyy/mm/dd';
  }

  return 'mm/dd/yyyy'; // fallback
}